import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown, FileSpreadsheet, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { cn } from '@/shared/utils/cn';
import type { ReferralListParty, ReferralListRow } from '../../../types/engagement-metrics.types';
import type { ReferralListSort } from '../../../hooks/useReferralList';
import {
  DIRECTION_LABELS,
  REFERRAL_COLUMNS,
  formatReferralDate,
  importBatchTip,
} from './referralsCopy';

type ColumnKey = keyof typeof REFERRAL_COLUMNS;

// Light app-green tooltip (the shared default is dark gray).
const TIP_THEME =
  'max-w-xs whitespace-normal border border-[#cfe0d6] bg-[#eef4f0] text-[#15543e] shadow-md';

const InfoTip = ({ text }: { text: string }) => (
  <Tooltip delayDuration={150}>
    <TooltipTrigger asChild>
      <button type="button" aria-label="More info" className="inline-flex text-gray-400 hover:text-gray-600">
        <Info className="h-3.5 w-3.5" />
      </button>
    </TooltipTrigger>
    <TooltipContent className={cn(TIP_THEME, 'font-normal normal-case tracking-normal')}>
      {text}
    </TooltipContent>
  </Tooltip>
);

const ROLE_BADGE: Record<string, { label: string; className: string }> = {
  SERVICE_PROVIDER: { label: 'Provider', className: 'bg-[#e4ede8] text-[#2c5c47]' },
  FINANCIAL_ADVISOR: { label: 'FP', className: 'bg-[#e2edf0] text-[#2c6e7d]' },
  CONCIERGE: { label: 'Concierge', className: 'bg-[#f3edd9] text-[#87702f]' },
};

const PartyCell = ({ party }: { party: ReferralListParty | null }) => {
  if (!party) return <span className="text-gray-400">—</span>;
  const badge = party.role ? ROLE_BADGE[party.role] : undefined;
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5">
        <span className="whitespace-nowrap font-medium text-gray-900">{party.name}</span>
        {badge && (
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[.3px]',
              badge.className,
            )}
          >
            {badge.label}
          </span>
        )}
        {party.external && (
          <span className="inline-flex items-center rounded border border-dashed border-gray-300 px-1.5 text-[10px] font-semibold uppercase text-gray-500">
            External
          </span>
        )}
      </div>
      {party.company && <div className="whitespace-nowrap text-xs text-gray-500">{party.company}</div>}
    </div>
  );
};

const SortIcon = ({ active, dir }: { active: boolean; dir: ReferralListSort['sortDir'] }) => {
  if (!active) return <ChevronsUpDown className="h-3.5 w-3.5 text-gray-300" />;
  return dir === 'desc' ? <ArrowDown className="h-3.5 w-3.5" /> : <ArrowUp className="h-3.5 w-3.5" />;
};

type ReferralsTableProps = {
  rows: ReferralListRow[];
  sort: ReferralListSort;
  onSort: (sortBy: ReferralListSort['sortBy']) => void;
  /**
   * Actions cell for manual rows (edit/delete, slices 19/20). When omitted the
   * Actions column is hidden; platform rows show "Read-only".
   */
  renderRowActions?: (row: ReferralListRow) => ReactNode;
  footer?: ReactNode;
};

// Actions pinned right so edit/delete stay reachable while the wide table scrolls horizontally.
const STICKY_ACTIONS_EDGE = 'sticky right-0 shadow-[inset_1px_0_0_theme(colors.gray.200)]';
const STICKY_ACTIONS_HEAD = `${STICKY_ACTIONS_EDGE} z-20`;
const STICKY_ACTIONS_CELL = `${STICKY_ACTIONS_EDGE} z-[1] bg-white group-hover:bg-gray-50`;

/** Referrals tab table: the 10 columns of ticket 04, in order. No links out (ticket 07). */
export const ReferralsTable = ({ rows, sort, onSort, renderRowActions, footer }: ReferralsTableProps) => {
  const th = (key: ColumnKey, sortBy?: ReferralListSort['sortBy'], extra?: string) => {
    const column = REFERRAL_COLUMNS[key];
    return (
      <th
        key={key}
        scope="col"
        aria-sort={sortBy && sort.sortBy === sortBy ? (sort.sortDir === 'desc' ? 'descending' : 'ascending') : undefined}
        className={cn(
          'sticky top-0 z-10 whitespace-nowrap bg-gray-50 px-3 py-2.5 text-left text-[11px] font-bold uppercase tracking-wide text-gray-500',
          extra,
        )}
      >
        <span className="inline-flex items-center gap-1">
          {sortBy ? (
            <button
              type="button"
              className="inline-flex items-center gap-1 uppercase hover:text-gray-800"
              onClick={() => onSort(sortBy)}
            >
              {column.label}
              <SortIcon active={sort.sortBy === sortBy} dir={sort.sortDir} />
            </button>
          ) : (
            column.label
          )}
          <InfoTip text={column.tip} />
        </span>
      </th>
    );
  };

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      {/* Bounded height: both scrollbars stay in view; header and Actions column are sticky. */}
      <div className="max-h-[calc(100vh-16rem)] min-h-[20rem] overflow-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-gray-200">
            <tr>
              {th('type')}
              {th('referred')}
              {th('referralDate', 'referralDate')}
              {th('referrer')}
              {th('careSeeker')}
              {th('onBehalfOf')}
              {th('origin')}
              {th('billable')}
              {th('createdDate', 'createdDate')}
              {renderRowActions && th('actions', undefined, STICKY_ACTIONS_HEAD)}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={renderRowActions ? 10 : 9} className="px-3 py-10 text-center text-gray-500">
                  No referrals match these filters.
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={row.rowKey} className="group border-t border-gray-100 align-top hover:bg-gray-50">
                <td className="px-3 py-3">
                  <span
                    className={cn(
                      'inline-flex whitespace-nowrap rounded-md px-2 py-1 text-xs font-semibold',
                      row.direction === 'CALL_REQUEST'
                        ? 'bg-amber-50 text-amber-800'
                        : 'bg-[#e4ede8] text-[#15543e]',
                    )}
                  >
                    {DIRECTION_LABELS[row.direction] ?? row.direction}
                  </span>
                </td>
                <td className="px-3 py-3"><PartyCell party={row.referred} /></td>
                <td className="whitespace-nowrap px-3 py-3 font-medium text-gray-900">
                  {formatReferralDate(row.referralDate)}
                </td>
                <td className="px-3 py-3"><PartyCell party={row.referrer} /></td>
                <td className="px-3 py-3"><PartyCell party={row.careSeeker} /></td>
                <td className="px-3 py-3"><PartyCell party={row.onBehalfOf} /></td>
                <td className="px-3 py-3">
                  <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-gray-700">
                    {row.origin === 'MANUAL' ? 'Manual' : 'Platform'}
                    {row.importBatch && (
                      <Tooltip delayDuration={150}>
                        <TooltipTrigger asChild>
                          <button type="button" aria-label="Bulk import details" className="text-[#2c6e7d]">
                            <FileSpreadsheet className="h-4 w-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className={TIP_THEME}>
                          {importBatchTip(row.importBatch)}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <span
                    className={cn(
                      'inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-bold leading-none',
                      row.isBillable ? 'bg-[#e3ede7] text-[#2c6a4a]' : 'bg-[#f0ede6] text-[#7f8a83]',
                    )}
                  >
                    {row.isBillable ? 'Billable' : 'Non-billable'}
                  </span>
                </td>
                <td className="whitespace-nowrap px-3 py-3 text-gray-600">
                  {formatReferralDate(row.createdDate)}
                </td>
                {renderRowActions && (
                  <td className={cn('px-3 py-3', STICKY_ACTIONS_CELL)}>
                    {row.manualReferralId ? (
                      renderRowActions(row)
                    ) : (
                      <span className="text-xs text-gray-400">Read-only</span>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {footer}
    </div>
  );
};
